import { useState } from "react";
import { Alert } from "@/ui/alert";
import { useAcoes, useEstado, useOcupado } from "../estado.jsx";
import { hora } from "../visual.jsx";
import { Botao, Cortina, SeloIA } from "./comuns.jsx";

/**
 * A leitura do caso pela IA — o único bloco escuro da coluna.
 *
 * O que saiu daqui: "RISCOS DETECTADOS" (era o MESMO prazo e o MESMO documento
 * que PENDÊNCIAS já mostra, recalculados e sem ordem de urgência),
 * "CONHECIMENTO DO CLIENTE" (as pastilhas eram os MESMOS campos da FICHA, dois
 * blocos abaixo — empresa, função, salário, admissão, escritos duas vezes na
 * mesma coluna) e as ações sugeridas, que foram para AGORA, embaixo do que o
 * servidor manda fazer: duas listas de "próximo passo" com o mesmo desenho
 * deixavam a pessoa sem saber qual clicar.
 *
 * O que ficou é o que só a IA sabe dizer: uma frase sobre o caso, o resumo, e o
 * placar dos fatos que vão para a peça. O rodapé diz QUANDO a leitura foi feita
 * — é o que separa "acabou de ler" de uma frase de meia hora atrás com cara de
 * texto fixo da tela.
 */
function Leitura({ ia, aoReler, ocupado }) {
  /*
   * WIDGET escuro que abre FOLHA — não colapsável.
   *
   * A regra da casa é "widget que aparece quando precisa, nunca colapsável"
   * (27/08), e aqui ela tem um motivo a mais: o que a IA entendeu do caso é
   * LEITURA, e leitura em coluna de 300px é três linhas cortadas com um "ler
   * tudo" embaixo. Na folha ela tem a largura de um parágrafo.
   *
   * Fechado o widget diz as três coisas que valem de relance — que a IA leu, o
   * que ela concluiu (uma linha) e o placar dos fatos. Toque abre o resto.
   */
  const [aberto, setAberto] = useState(false);
  /* Fato nenhum marcado não vira "0/0": zero sobre zero é o placar de um jogo
     que não começou, e no cartão parecia contagem quebrada. */
  const numeros = [
    ["ÊXITO", ia.exito, "text-verde-claro"],
    ["FATOS NA PEÇA", ia.fatos?.startsWith("0/0") ? null : ia.fatos, "text-creme"],
  ].filter(([, valor]) => valor);
  /* `recado` é a exceção CRUA do motor ("Nenhum agente local conectado. Abra
     Ajustes → …"). Ela não é leitura do caso: é a IA dizendo que não leu. */
  const falhou = Boolean(ia.recado);

  return (
    <>
      <button
        type="button"
        data-secao="copiloto"
        onClick={() => setAberto(true)}
        data-dica="A leitura deste caso pela IA — resumo, êxito estimado e o placar dos fatos"
        className="mt-3 flex w-full cursor-pointer flex-col gap-1.5 rounded-[18px] border border-transparent bg-foreground p-[11px_13px] text-left text-creme transition-transform duration-[var(--t-toque)] ease-entra active:scale-[.985]"
      >
        <span className="flex w-full items-center gap-2.5">
          <SeloIA
            fundo={falhou ? "var(--color-ambar)" : "var(--color-verde-vivo)"}
            letra="var(--color-tinta)"
            tamanho={18}
            fonte={9}
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-apoio leading-[1.3] font-semibold text-creme">
              {falhou ? "A IA não leu este caso" : ia.titulo}
            </span>
            <span className="mt-px block truncate text-selo text-creme/45">
              {falhou
                ? "o resumo é do banco, não do modelo — toque para ver o motivo"
                : ia.deMemoria && ia.pensadoEm
                  ? `lido às ${hora(ia.pensadoEm)}`
                  : "lido agora"}
            </span>
          </span>
          {numeros.length ? (
            <span className="flex flex-none items-baseline gap-2">
              {numeros.map(([rotulo, valor, cor]) => (
                <span key={rotulo} className={`font-mono text-apoio ${cor}`} data-dica={rotulo}>
                  {valor}
                </span>
              ))}
            </span>
          ) : null}
        </span>

        {/* O RESUMO na cara, e não atrás do toque.
            A linha dizia que a IA leu e escondia o que ela leu — quem abre o
            caso quer a leitura, não a notícia de que existe uma. Três linhas é o
            que cabe na coluna sem empurrar os widgets para baixo da dobra; o
            texto inteiro continua na folha, que tem largura de parágrafo. */}
        {!falhou && ia.resumo ? (
          /* INTEIRO, sem "ler tudo": o resumo é a leitura do caso, e cortá-lo em
             três linhas devolvia a pergunta ("o que mais ela viu?") ao clique. */
          <span className="text-apoio leading-[1.5] text-creme/72">{ia.resumo}</span>
        ) : null}
      </button>

      {aberto ? (
        <Cortina
          aoFechar={() => setAberto(false)}
          largura="520px"
          z={44}
          titulo={falhou ? "A IA não leu este caso" : "O que a IA entendeu"}
          antesDoTitulo={<SeloIA fundo="var(--color-verde)" letra="var(--color-creme)" tamanho={22} fonte={10} />}
          acima={ia.deMemoria && ia.pensadoEm ? `Lido às ${hora(ia.pensadoEm)} — nada mudou no caso desde então.` : "Lido agora."}
          rodape={
            <Botao
              rotulo={ocupado ? "Lendo…" : "Ler de novo"}
              aoTocar={aoReler}
              desabilitado={ocupado}
              titulo="Ler o caso de novo, ignorando a leitura guardada"
            />
          }
        >
          {numeros.length ? (
            <div className="mb-3 flex items-baseline gap-4">
              {numeros.map(([rotulo, valor]) => (
                <span key={rotulo} className="flex items-baseline gap-1.5">
                  <span className="text-selo tracking-[.05em] text-muted-foreground">{rotulo}</span>
                  <span className="font-mono text-forte font-bold text-verde-fundo">{valor}</span>
                </span>
              ))}
            </div>
          ) : null}

          <div className="font-serif text-[20px] leading-[1.25] text-balance">{ia.titulo}</div>
          <p className="mt-2 text-corpo leading-[1.6] text-tinta-media">{ia.resumo}</p>

          {/* O texto técnico da falha: ele fala de terminal e de arquivo de
              configuração, e quem atende não resolve isso — quem cuida do
              servidor resolve, e é essa pessoa que abre esta folha. */}
          {falhou ? (
            <Alert
              role="status"
              className="mt-3.5 rounded-xl border-ambar/35 bg-ambar/11 p-3 text-apoio leading-[1.5] text-ambar-escuro"
            >
              {ia.recado}
            </Alert>
          ) : null}
        </Cortina>
      ) : null}
    </>
  );
}

export default function Copiloto() {
  const { ia, motor, caso } = useEstado();
  const ocupado = useOcupado();
  const { relerCopiloto } = useAcoes();

  /* Sem IA é escolha do escritório: o servidor lê o caso só do banco, e a linha
     "o que a IA entendeu" não aparece — nem o esqueleto, que prometeria uma
     leitura que ninguém vai fazer. */
  if (caso?.modo_efetivo === "sem_ia" || ia?.semIA) return null;

  if (!ia)
    return (
      /* Esqueleto, e não a frase "consultando…": o cartão escuro ocupa o mesmo
         espaço que vai ocupar, e a troca é o bloco ganhando conteúdo em vez de a
         coluna inteira se remontando quando a leitura chega. */
      <div
        data-secao="copiloto"
        role="status"
        aria-label={`Consultando o copiloto${motor?.nome ? ` (${motor.nome})` : ""}…`}
        className="mt-3.5 animate-[vPulse_1.6s_ease-in-out_infinite] rounded-2xl bg-foreground/[.86] p-[15px_16px]"
      >
        <div className="h-2 w-24 rounded-full bg-creme/18" />
        <div className="mt-3 h-3.5 w-4/5 rounded-full bg-creme/14" />
        <div className="mt-2 h-2.5 w-full rounded-full bg-creme/9" />
        <div className="mt-1.5 h-2.5 w-2/3 rounded-full bg-creme/9" />
      </div>
    );

  if (ia.erro)
    return (
      <div data-secao="copiloto" className="mt-3.5">
        {/* `role="status"` e não o `alert` que o componente traz por padrão: o
            copiloto falhando não é resposta ao que a pessoa acabou de fazer — ela
            pode estar digitando para o cliente, e interromper a leitura com um
            aviso do assistente é tirar a atenção do que importa. */}
        <Alert role="status" className="rounded-xl border-ambar/30 bg-ambar/11 p-3 text-corpo leading-[1.5] text-ambar-escuro">
          {ia.erro}
        </Alert>
      </div>
    );

  return <Leitura ia={ia} aoReler={relerCopiloto} ocupado={ocupado === "copiloto"} />;
}
